/**
 * Settings, persisted to localStorage.
 *
 * Kate changed her mind about the attack key mid-conversation — first "you press R if
 * you want to kill something or bite it", then, when asked whether it had to be R,
 * "you can choose a random button like in settings". So the binding is data, not a
 * constant, and Settings owns it. She said the same about touch: "In settings, can you
 * change like how you use your controller on your iPad? Yes, you can."
 */

export type Handedness = 'right' | 'left'

export interface Settings {
  /** KeyboardEvent.code for the attack/bite action. */
  attackKey: string
  /** Which side of the screen the D-pad sits on. */
  touchHandedness: Handedness
  /** Scale applied to the on-screen touch controls. */
  touchScale: number
  /** Mouse look sensitivity. */
  lookSensitivity: number
  /** Offline difficulty — Kate: "you can choose your difficulty" (M6). */
  difficulty: 'easy' | 'normal' | 'hard'
}

const STORAGE_KEY = 'animal-attack:settings'

export const DEFAULT_SETTINGS: Settings = {
  attackKey: 'KeyR',
  touchHandedness: 'left',
  touchScale: 1,
  lookSensitivity: 1,
  difficulty: 'normal',
}

function load(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_SETTINGS }
    const parsed = JSON.parse(raw) as Partial<Settings>
    // Merge over defaults so a settings file from an older build still loads.
    return { ...DEFAULT_SETTINGS, ...parsed }
  } catch {
    return { ...DEFAULT_SETTINGS }
  }
}

export class SettingsStore {
  private value: Settings = load()
  private readonly listeners = new Set<(s: Settings) => void>()

  get current(): Readonly<Settings> {
    return this.value
  }

  set<K extends keyof Settings>(key: K, value: Settings[K]): void {
    this.value = { ...this.value, [key]: value }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.value))
    } catch {
      // Private browsing can refuse writes; the setting still applies this session.
    }
    for (const listener of this.listeners) listener(this.value)
  }

  reset(): void {
    this.value = { ...DEFAULT_SETTINGS }
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Ignore.
    }
    for (const listener of this.listeners) listener(this.value)
  }

  subscribe(listener: (s: Settings) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
}

/** Human-readable label for a KeyboardEvent.code, for the Settings screen. */
export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3)
  if (code.startsWith('Digit')) return code.slice(5)
  if (code.startsWith('Arrow')) return `${code.slice(5)} arrow`
  const named: Record<string, string> = {
    Space: 'Space',
    ShiftLeft: 'Left Shift',
    ShiftRight: 'Right Shift',
    ControlLeft: 'Left Ctrl',
    ControlRight: 'Right Ctrl',
    AltLeft: 'Left Alt',
    AltRight: 'Right Alt',
    Enter: 'Enter',
    Tab: 'Tab',
  }
  return named[code] ?? code
}
